import React, { createContext, useCallback, useEffect, useRef, useState, useContext } from 'react';

const IncomingWebhooksContext = createContext();

export function IncomingWebhooksProvider({ children }) {
  const [loading, setLoading] = useState(false);
  const [events, setEvents] = useState([]);
  const [error, setError] = useState(null);
  const refreshInFlightRef = useRef(false);
  const eventsRef = useRef(events);

  const [incomingSettings, setIncomingSettings] = useState([]);
  const [incomingSettingsLoading, setIncomingSettingsLoading] = useState(false);
  const [incomingSettingsError, setIncomingSettingsError] = useState(null);
  const [incomingSettingsSaving, setIncomingSettingsSaving] = useState(false);
  const incomingSettingsInFlightRef = useRef(false);

  const clearIncomingSettingsError = useCallback(() => {
    setIncomingSettingsError(null);
  }, []);

  useEffect(() => {
    eventsRef.current = events;
  }, [events]);

  const refreshEvents = useCallback(async ({ from, to } = {}) => {
    if (refreshInFlightRef.current) {
      return eventsRef.current;
    }

    refreshInFlightRef.current = true;
    const token = localStorage.getItem('token');
    if (!token) {
      setEvents([]);
      setError('Missing access token');
      refreshInFlightRef.current = false;
      return [];
    }

    try {
      setLoading(true);
      setError(null);

      const webhooksApiUrl = process.env.REACT_APP_WEBHOOKS_API_URL || '';
      const url = new URL(`${webhooksApiUrl}/v1/webhooks/datalake`, window.location.origin);
      if (from) url.searchParams.set('from_timestamp', from);
      if (to) url.searchParams.set('to_timestamp', to);

      const res = await fetch(url.toString(), {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setEvents(list);
        return list;
      }

      setEvents([]);
      setError(`Failed to load incoming webhooks (${res.status})`);
      return [];
    } catch (err) {
      console.error('Failed to pull incoming webhook events:', err);
      setEvents([]);
      setError('Failed to pull incoming webhook events');
      return [];
    } finally {
      setLoading(false);
      refreshInFlightRef.current = false;
    }
  }, []);

  const refreshIncomingSettings = useCallback(async () => {
    if (incomingSettingsInFlightRef.current) {
      return;
    }

    incomingSettingsInFlightRef.current = true;
    const token = localStorage.getItem('token');
    if (!token) {
      setIncomingSettings([]);
      setIncomingSettingsError('Missing access token');
      incomingSettingsInFlightRef.current = false;
      return [];
    }

    try {
      setIncomingSettingsLoading(true);
      setIncomingSettingsError(null);

      const webhooksApiUrl = process.env.REACT_APP_WEBHOOKS_API_URL || '';
      const res = await fetch(`${webhooksApiUrl}/v1/webhooks/incoming/settings`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      if (res.ok) {
        const data = await res.json();
        const list = Array.isArray(data) ? data : [];
        setIncomingSettings(list);
        return list;
      }

      let message = `Failed to load incoming settings (${res.status})`;
      try {
        const body = await res.json();
        if (body?.detail) {
          message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
        }
      } catch {
        /* ignore */
      }
      setIncomingSettings([]);
      setIncomingSettingsError(message);
      return [];
    } catch (err) {
      console.error('Failed to pull incoming webhook settings:', err);
      setIncomingSettings([]);
      setIncomingSettingsError('Failed to pull incoming webhook settings');
      return [];
    } finally {
      setIncomingSettingsLoading(false);
      incomingSettingsInFlightRef.current = false;
    }
  }, []);

  const createIncomingSetting = useCallback(async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIncomingSettingsError('Missing access token');
      return { ok: false };
    }

    try {
      setIncomingSettingsSaving(true);
      setIncomingSettingsError(null);
      const webhooksApiUrl = process.env.REACT_APP_WEBHOOKS_API_URL || '';
      const res = await fetch(`${webhooksApiUrl}/v1/webhooks/incoming/settings`, {
        method: 'PUT',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        await refreshIncomingSettings();
        return { ok: true };
      }

      let message = `Create failed (${res.status})`;
      try {
        const body = await res.json();
        if (body?.detail) {
          message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
        }
      } catch {
        /* ignore */
      }
      setIncomingSettingsError(message);
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Create incoming webhook setting failed:', err);
      setIncomingSettingsError('Network error');
      return { ok: false };
    } finally {
      setIncomingSettingsSaving(false);
    }
  }, [refreshIncomingSettings]);

  const updateIncomingSetting = useCallback(async (payload) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIncomingSettingsError('Missing access token');
      return { ok: false };
    }

    try {
      setIncomingSettingsSaving(true);
      setIncomingSettingsError(null);
      const webhooksApiUrl = process.env.REACT_APP_WEBHOOKS_API_URL || '';
      const res = await fetch(`${webhooksApiUrl}/v1/webhooks/incoming/settings`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(payload),
      });

      if (res.ok) {
        await refreshIncomingSettings();
        return { ok: true };
      }

      let message = `Update failed (${res.status})`;
      try {
        const body = await res.json();
        if (body?.detail) {
          message = typeof body.detail === 'string' ? body.detail : JSON.stringify(body.detail);
        }
      } catch {
        /* ignore */
      }
      setIncomingSettingsError(message);
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Update incoming webhook setting failed:', err);
      setIncomingSettingsError('Network error');
      return { ok: false };
    } finally {
      setIncomingSettingsSaving(false);
    }
  }, [refreshIncomingSettings]);

  const deleteIncomingSetting = useCallback(async (settingsId) => {
    const token = localStorage.getItem('token');
    if (!token) {
      setIncomingSettingsError('Missing access token');
      return { ok: false };
    }

    try {
      setIncomingSettingsSaving(true);
      setIncomingSettingsError(null);
      const webhooksApiUrl = process.env.REACT_APP_WEBHOOKS_API_URL || '';
      const res = await fetch(
        `${webhooksApiUrl}/v1/webhooks/incoming/settings/${encodeURIComponent(String(settingsId))}`,
        {
          method: 'DELETE',
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );

      let data;
      const ct = res.headers.get('content-type') || '';
      if (ct.includes('application/json')) {
        try {
          data = await res.json();
        } catch {
          data = undefined;
        }
      }

      if (res.ok && (data === true || (res.status === 204 && data === undefined))) {
        await refreshIncomingSettings();
        return { ok: true };
      }

      if (res.ok && data === false) {
        setIncomingSettingsError('Could not delete this setting.');
        return { ok: false };
      }

      let message = `Delete failed (${res.status})`;
      if (data != null && typeof data === 'object' && data.detail) {
        message = typeof data.detail === 'string' ? data.detail : JSON.stringify(data.detail);
      } else if (typeof data === 'string') {
        message = data;
      }
      setIncomingSettingsError(message);
      return { ok: false, status: res.status };
    } catch (err) {
      console.error('Delete incoming webhook setting failed:', err);
      setIncomingSettingsError('Network error');
      return { ok: false };
    } finally {
      setIncomingSettingsSaving(false);
    }
  }, [refreshIncomingSettings]);

  useEffect(() => {
    refreshEvents();
  }, [refreshEvents]);

  useEffect(() => {
    refreshIncomingSettings();
  }, [refreshIncomingSettings]);

  return (
    <IncomingWebhooksContext.Provider
      value={{
        events,
        refreshEvents,
        loading,
        error,
        incomingSettings,
        refreshIncomingSettings,
        createIncomingSetting,
        updateIncomingSetting,
        deleteIncomingSetting,
        incomingSettingsLoading,
        incomingSettingsError,
        incomingSettingsSaving,
        clearIncomingSettingsError,
      }}
    >
      {children}
    </IncomingWebhooksContext.Provider>
  );
}

export function useIncomingWebhooks() {
  return useContext(IncomingWebhooksContext);
}
